import { customEvent } from '@genesislcap/foundation-events';
import { classNames } from '@microsoft/fast-web-utilities';
import { attr, customElement, html, css, FoundationElement, observable, ref, when } from '@genesislcap/web-core';
import { 
  orderAmountFormatter, 
  allowOnlyWholePositiveNumbers,
  hasNumericValueChanged,
  calculateCaretPositionAfterSanitization
} from '../deal-series/deal-series.util';
import { GridApi, Column, IRowNode } from '@ag-grid-community/core';

@customElement({
  name: 'maturity-amount-entry',
  template: html<MaturityAmountEntry>`
    <div
      class="maturity-amount-entry ${(x) =>
        classNames(
          ['all', x.allActive],
          ['focused', x.focused],
          ['disabled', x.disabled],
          ['split-active', x.splitActive],
        )}"
    >
      <div class="all-controls">
        <rapid-button class="all-button" @click="${(x) => x.handleAllClicked()}">All</rapid-button>
        ${when(
          (x) => x.showSplitButton,
          html<MaturityAmountEntry>`
            <button
              type="button"
              class="split-icon-button"
              aria-label="Split order"
              title="Split order"
              @click="${(x, ctx) => {
                ctx.event.preventDefault();
                ctx.event.stopPropagation();
                x.handleSplitClicked();
              }}"
              @mousedown="${(_, ctx) => ctx.event.stopPropagation()}"
            >
              <img src="/deal-icons/split.svg" alt="" />
            </button>
          `,
        )}
      </div>
      <div class="input-container">
        <input
          ${ref('maturityInput') as HTMLInputElement}
          @focus="${x => x.handleFocus()}"
          @blur="${x => x.handleBlur()}"
          @click="${x => x.focusInput()}"
          ?disabled="${x => x.allActive || x.disabled || x.amountReadonly}"
          @input="${(x, ctx) => x.handleInput(customEvent(ctx))}"
          @change="${(x, ctx) => x.handleChange(customEvent(ctx))}" 
          value="${x => x.value}"
        >
      </div>
    </div>
  `,
  styles: css`
    .maturity-amount-entry {
      display: flex;
      align-items: center;
      gap: 10px;
      height: 100%;
      width: 100%;
      position: relative;
    }

    .all-controls {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }

    .input-container {
      flex: 1;

      input {
        width: 100%;
        background: transparent;
        outline: none;
        border: none;
        text-align: right;
      }
    }

    rapid-button {
      opacity: 0;
      height: fit-content;
    }

    rapid-button::part(control) {
      background: transparent;
      border-color: #fff;
      padding: 8px 0;
      font-size: 12px;
      line-height: 1.2;
    }

    :host(:not([suppress-all-hover]):hover) rapid-button,
    :host(:not([suppress-all-hover]):hover) .split-icon-button,
    .maturity-amount-entry.focused rapid-button,
    .maturity-amount-entry.focused .split-icon-button {
      opacity: 1;
    }

    .maturity-amount-entry.all {
      .all-button::part(control) {
        background: rgba(135, 155, 166, 0.7);
      }

      rapid-button {
        opacity: 1;
      }

      input {
        color: rgba(201, 201, 201, 0.498);
      }
    }

    .split-icon-button {
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      padding: 4px;
      margin: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      border-radius: 3px;
      line-height: 0;
    }

    .split-icon-button img {
      width: 24px;
      height: 24px;
      display: block;
    }

    .split-icon-button:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    .maturity-amount-entry.split-active .split-icon-button {
      opacity: 1;
    }

    /* Hide All and split when disabled. */
    .maturity-amount-entry.disabled rapid-button,
    .maturity-amount-entry.disabled .split-icon-button {
      opacity: 0 !important;
      pointer-events: none;
    }

    .maturity-amount-entry.split-active.disabled .split-icon-button {
      opacity: 1 !important;
      pointer-events: auto;
    }
  `,
})
export class MaturityAmountEntry extends FoundationElement {
  @observable focused: boolean;

  // AG Grid context for cell navigation
  gridApi: GridApi;
  node: IRowNode;
  column: Column;

  // Previous value to detect actual changes (prevents saves when normalized values are equal)
  private previousValue: string | number | null = null;

  @attr({ attribute: 'max-amount' }) maxAmount: number;

  maturityInput: HTMLInputElement;

  @attr value: number | string;
  valueChanged(oldValue, newValue) {
    if (this.maturityInput) {
      this.maturityInput.value = newValue;
    }
    this.previousValue = newValue;
  }

  @attr({ attribute: 'all-active', mode: 'boolean' }) allActive: boolean;
  @attr({ attribute: 'disabled', mode: 'boolean' }) disabled: boolean;
  @attr({ attribute: 'show-split-button', mode: 'boolean' }) showSplitButton: boolean;
  @attr({ attribute: 'split-active', mode: 'boolean' }) splitActive: boolean;
  @attr({ attribute: 'suppress-all-hover', mode: 'boolean' }) suppressAllHover: boolean;
  @attr({ attribute: 'all-display-only', mode: 'boolean' }) allDisplayOnly: boolean;
  @attr({ attribute: 'amount-readonly', mode: 'boolean' }) amountReadonly: boolean;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.handleKeyDown);
    this.previousValue = this.value;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.handleKeyDown);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // Prevent AG Grid from handling arrow keys when editing
    if (this.focused && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.stopPropagation();
    }

    // Enter key: save value and navigate to next row
    if (this.focused && event.key === 'Enter') {
      event.preventDefault();
      /*
      Killing the event here after the cursor has moved position on the input component.
      This is stopping the Left and Right keyboard events from being picked up by the parent AgGrid
      which is moving focus to the next ag grid cell instead of just moving the input cursor position.
       */
      event.stopPropagation();
      
      // Simply blur the input - the blur handler will trigger save if value changed
      // This ensures setData is only called when cell is no longer focused
      if (this.maturityInput && !this.disabled) {
        this.maturityInput.blur();
      }
      
      // Move focus to next row after blur completes
      if (this.gridApi && this.node && this.column) {
        const rowIndex = this.node.rowIndex;
        const rowCount = this.gridApi.getDisplayedRowCount();
        const targetRow = rowIndex + 1 < rowCount ? rowIndex + 1 : rowIndex;
        this.gridApi.setFocusedCell(targetRow, this.column);
      }
    }
  }

  handleSplitClicked(): void {
    if (this.disabled && !this.splitActive) {
      return;
    }
    this.$emit('split');
  }

  handleAllClicked(): void {
    if (this.disabled || this.allDisplayOnly) {
      return;
    }
    this.allActive = !this.allActive;
    if (this.allActive) {
      this.value = orderAmountFormatter(this.maxAmount);
    } else {
      this.value = '';
      if (this.maturityInput) {
        this.maturityInput.value = '';
      }
      this.previousValue = '';
    }
    this.$emit('all', this.allActive);
  }

  focusInput(): void {
    if (this.disabled || this.amountReadonly || !this.maturityInput) {
      return;
    }
    this.maturityInput.focus();
  }

  handleInput(e: CustomEvent): void {
    if (this.disabled || this.amountReadonly || !this.maturityInput) {
      return;
    }
    // Keep only digits (drop decimals/symbols) while preserving caret position
    const cursorPosition = this.maturityInput.selectionStart ?? 0;
    const originalValue = this.maturityInput.value;
    const sanitizedValue = allowOnlyWholePositiveNumbers(originalValue);
    
    if (originalValue !== sanitizedValue) {
      const newCursorPosition = calculateCaretPositionAfterSanitization(originalValue, cursorPosition);
      this.maturityInput.value = sanitizedValue;
      this.maturityInput.setSelectionRange(newCursorPosition, newCursorPosition);
    }
  }

  handleChange(e: CustomEvent): void {
    if (this.disabled) {
      return;
    }
    // Defer change handling to blur to avoid saving while editing
    e.stopPropagation();
  }

  handleFocus(): void {
    if (this.disabled || this.amountReadonly) {
      return;
    }
    this.focused = true;
    if (this.maturityInput) {
      // Store unformatted digits for change detection
      this.previousValue = allowOnlyWholePositiveNumbers(this.maturityInput.value);
      // Show unformatted digits while editing
      this.maturityInput.value = this.previousValue;
    }
  }

  handleBlur(): void {
    this.focused = false;
    if (this.maturityInput && !this.disabled && !this.amountReadonly) {
      const currentValue = this.maturityInput.value.trim();
      
      if (currentValue !== '') {
        this.maturityInput.value = orderAmountFormatter(currentValue);
      }
      
      if (this.hasValueChanged(currentValue)) {
        this.triggerSave(currentValue);
      }
    }
  }

  private hasValueChanged(newValue: string): boolean {
    return hasNumericValueChanged(newValue, this.previousValue);
  }

  private triggerSave(value: string): void {
    // Save on blur when a change is detected
    this.$emit('change', value);
    this.previousValue = value;
  }
}
